import { useRef, useState } from 'react';
import { C, mono } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../api';
import type { DocumentRecord, CriticalDateRecord, RequestRecord } from '../../api';
import { useAsync } from '../../useAsync';
import { fmtDate } from '../../format';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const DOC_KINDS = ['lease', 'id', 'gst', 'noc', 'insurance', 'receipt', 'invoice', 'other'] as const;

/** Owner-facing kind labels, tuned to the Indian paperwork a tenancy carries. */
const KIND_LABEL: Record<string, string> = {
  lease: 'Lease deed',
  id: 'ID (Aadhaar / PAN)',
  gst: 'GST / TDS',
  noc: 'NOC / KYC',
  insurance: 'Insurance',
  receipt: 'Receipt',
  invoice: 'Invoice',
  other: 'Other',
};

function fmtSize(bytes?: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Days until an epoch-ms date; negative = already past. */
function daysUntil(ms: number): number {
  return Math.ceil((ms - Date.now()) / 86_400_000);
}

/** An amber/red pill for a document's expiry, or null if none / far off. */
function ExpiryChip({ expiryDate }: { expiryDate?: number | null }) {
  if (!expiryDate) return null;
  const d = daysUntil(expiryDate);
  const past = d < 0;
  const soon = d >= 0 && d <= 90;
  const bg = past ? '#fbe7e3' : soon ? '#fbf0dd' : C.soft;
  const fg = past ? C.red : soon ? '#9a6a12' : C.muted;
  const label = past ? `Expired ${fmtDate(expiryDate)}` : `Expires ${fmtDate(expiryDate)}`;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: fg, background: bg, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

const chipShared: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: C.hi, background: C.greenSoft, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' };
const chipTenant: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: C.accent2, background: C.soft, borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' };

// ===========================================================================
// TenancyDocuments — per-tenancy vault + inline upload
// ===========================================================================
export function TenancyDocuments({ tenancyId, tenantName }: { tenancyId: string; tenantName?: string }) {
  const { data, loading, error, reload } = useAsync<DocumentRecord[]>(() => api.documents({ tenancyId }), [tenancyId]);
  const [showUpload, setShowUpload] = useState(false);

  const docs = [...(data ?? [])].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12.8, color: C.muted }}>{data ? `${docs.length} on file for this tenancy` : ' '}</span>
        <button onClick={() => setShowUpload((s) => !s)} style={primaryBtn}>
          <Icon n={showUpload ? 'chev' : 'doc'} size={15} color="#fff" style={showUpload ? { transform: 'rotate(-90deg)' } : undefined} />
          {showUpload ? 'Close' : 'Upload'}
        </button>
      </div>

      {showUpload && (
        <UploadPanel
          tenancyId={tenancyId}
          tenantName={tenantName}
          onDone={() => {
            setShowUpload(false);
            reload();
          }}
        />
      )}

      {loading ? (
        <Loading label="Loading documents…" minHeight={220} />
      ) : error ? (
        <Empty icon="doc" title="Documents unavailable" hint="We couldn't reach the vault." minHeight={200}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : docs.length === 0 ? (
        <Empty icon="doc" title="No documents yet" hint="Attach the lease deed, the tenant's Aadhaar/PAN, the GST certificate or an insurance policy." minHeight={200}>
          <button onClick={() => setShowUpload(true)} style={retryBtn}>Upload a document</button>
        </Empty>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
          {docs.map((d, i) => {
            const meta = [KIND_LABEL[d.kind] ?? d.kind, fmtSize(d.size), d.createdAt ? fmtDate(d.createdAt) : ''].filter(Boolean).join(' · ');
            return (
              <div
                key={d.id}
                onClick={() => window.open(api.documentContentUrl(d.id), '_blank', 'noopener')}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderTop: i ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}
              >
                <span style={{ width: 36, height: 36, borderRadius: 10, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                  <Icon n="doc" size={16} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ color: C.ink, fontWeight: 600, fontSize: 13.8, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</b>
                  <small style={{ color: C.muted, fontSize: 12 }}>{meta}</small>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <ExpiryChip expiryDate={d.expiryDate} />
                  {d.uploadedByRole === 'tenant' && <span style={chipTenant}>By tenant</span>}
                  {d.sharedWithTenant ? <span style={chipShared}>Shared</span> : <span style={{ ...chipTenant, color: C.muted, background: C.line2 }}>Private</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UploadPanel({ tenancyId, tenantName, onDone }: { tenancyId: string; tenantName?: string; onDone: () => void }) {
  const notify = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<(typeof DOC_KINDS)[number]>('lease');
  const [shared, setShared] = useState(false);
  const [expiry, setExpiry] = useState(''); // yyyy-mm-dd
  const [busy, setBusy] = useState(false);

  const canSubmit = !!file && !busy;

  async function submit() {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenancyId', tenancyId);
      fd.append('kind', kind);
      fd.append('title', title.trim() || file.name);
      if (shared) fd.append('shared_with_tenant', 'true');
      if (expiry) {
        const ms = new Date(`${expiry}T00:00:00`).getTime();
        if (Number.isFinite(ms)) fd.append('expiry_date', String(ms));
      }
      await api.uploadDocument(fd);
      notify(kind === 'insurance' && expiry ? 'Insurance filed. Renewal reminder set.' : 'Document uploaded');
      onDone();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: 15, display: 'flex', flexDirection: 'column', gap: 11 }}>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', border: `1.5px dashed ${C.line}`, borderRadius: 10, background: C.bg, cursor: 'pointer', textAlign: 'left', color: file ? C.ink : C.muted, fontSize: 13.5 }}
      >
        <Icon n="doc" size={17} color={C.accent2} />
        <span style={{ flex: 1, ...mono({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>{file ? file.name : 'Choose a file…'}</span>
        {file && <span style={{ color: C.muted, fontSize: 12 }}>{fmtSize(file.size)}</span>}
      </button>
      <input
        ref={fileRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
        }}
      />

      <label style={fieldLabel}>Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={tenantName ? `e.g. ${tenantName} lease deed` : 'e.g. Lease deed'} style={input} />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={fieldLabel}>Kind
          <select value={kind} onChange={(e) => setKind(e.target.value as (typeof DOC_KINDS)[number])} style={input}>
            {DOC_KINDS.map((k) => (<option key={k} value={k}>{KIND_LABEL[k]}</option>))}
          </select>
        </label>
        <label style={fieldLabel}>{kind === 'insurance' ? 'Policy expiry' : 'Expiry (optional)'}
          <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={input} />
        </label>
      </div>

      {kind === 'insurance' && (
        <div style={{ fontSize: 12, color: C.muted, marginTop: -4 }}>
          <Icon n="bell" size={12} color={C.accent} /> An expiry date sets an automatic renewal reminder (90/60/30 days out).
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: C.ink2, cursor: 'pointer' }}>
        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
        Share with the tenant <span style={{ color: C.muted }}>(off keeps it private to you)</span>
      </label>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'default' }}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// TenancyDates — the tenancy's key dates, derived from the lease + editable
// ===========================================================================
const DATE_TYPE_LABEL: Record<string, string> = {
  renewal: 'Renewal', escalation: 'Escalation', insurance: 'Insurance', registration: 'Registration',
  notice_window: 'Notice window', lock_in: 'Lock-in', rent_due: 'Rent due', custom: 'Custom',
};

export function TenancyDates({ tenancyId }: { tenancyId: string }) {
  const notify = useToast();
  const { data, loading, error, reload } = useAsync<CriticalDateRecord[]>(() => api.criticalDates({ tenancyId }), [tenancyId]);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const dates = [...(data ?? [])]
    .filter((d) => d.status !== 'dismissed')
    .sort((a, b) => (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0) || (a.dueDate ?? Infinity) - (b.dueDate ?? Infinity));

  async function sync() {
    setSyncing(true);
    try {
      const r = await api.deriveTenancyDates(tenancyId);
      notify(r.created > 0 ? `Added ${r.created} date${r.created === 1 ? '' : 's'} from the lease` : 'Lease dates already up to date');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not sync from the lease');
    } finally {
      setSyncing(false);
    }
  }
  async function markDone(id: string) {
    setBusyId(id);
    try {
      await api.updateCriticalDate(id, { status: 'done' });
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update');
    } finally {
      setBusyId(null);
    }
  }

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 12.8, color: C.muted }}>{data ? `${dates.filter((d) => d.status === 'upcoming').length} on the radar` : ' '}</span>
      <button onClick={sync} disabled={syncing} style={{ ...primaryBtn, opacity: syncing ? 0.6 : 1 }}>
        <Icon n="bell" size={14} color="#fff" /> {syncing ? 'Syncing…' : 'Sync from lease'}
      </button>
    </div>
  );

  if (loading) return <Loading label="Loading key dates…" minHeight={200} />;
  if (error)
    return (
      <Empty icon="bell" title="Key dates unavailable" hint="We couldn't load this tenancy's dates." minHeight={200}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  if (dates.length === 0)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {header}
        <Empty icon="bell" title="No key dates yet" hint="Set the lease's end, lock-in or escalation date and tap “Sync from lease” to generate the renewal, notice and escalation reminders." minHeight={180} />
      </div>
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {header}
      {dates.map((d) => {
        const dd = d.dueDate ? daysUntil(d.dueDate) : null;
        const done = d.status === 'done';
        const overdue = !done && dd != null && dd < 0;
        const soon = !done && dd != null && dd >= 0 && dd <= 30;
        return (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: '11px 14px', opacity: done ? 0.62 : 1 }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: done ? C.greenSoft : C.soft, color: done ? C.green : C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <Icon n={done ? 'check' : 'bell'} size={15} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <b style={{ color: C.ink, fontWeight: 600, fontSize: 13.8 }}>{d.title}</b>
                {d.note === 'auto:lease' && <span style={{ ...chipTenant, fontSize: 10, fontWeight: 700, color: C.accent2, textTransform: 'uppercase', letterSpacing: '.04em' }}>Auto</span>}
              </div>
              <small style={{ color: C.muted, fontSize: 12 }}>
                {(DATE_TYPE_LABEL[d.type] ?? d.type)}{d.dueDate ? ` · ${fmtDate(d.dueDate)}` : ''}{d.assignee ? ` · ${d.assignee}` : ''}
              </small>
            </div>
            {done ? (
              <span style={{ ...chipTenant, color: C.green, background: C.greenSoft }}>Done</span>
            ) : (
              <>
                {overdue ? (
                  <span style={{ ...chipTenant, color: C.red, background: '#fbe7e3' }}>{Math.abs(dd!)}d overdue</span>
                ) : soon ? (
                  <span style={{ ...chipTenant, color: '#9a6a12', background: '#fbf0dd' }}>in {dd}d</span>
                ) : dd != null ? (
                  <span style={{ ...chipTenant, color: C.muted, background: C.soft }}>in {dd}d</span>
                ) : null}
                <button onClick={() => markDone(d.id)} disabled={busyId === d.id} aria-label="Mark done" style={{ ...ghostSm, opacity: busyId === d.id ? 0.6 : 1 }}>
                  <Icon n="check" size={13} /> Done
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ===========================================================================
// TenancyRequests — read-only maintenance for this tenancy (Stage 5 makes live)
// ===========================================================================
const REQ_STATUS_LABEL: Record<string, string> = {
  submitted: 'New', seen: 'Seen', assigned: 'Assigned', in_progress: 'In progress', done: 'Done',
};

export function TenancyRequests({ tenancyId }: { tenancyId: string }) {
  const { data, loading, error, reload } = useAsync<RequestRecord[]>(() => api.ownerRequests(), []);
  const reqs = (data ?? []).filter((r) => r.tenancyId === tenancyId).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

  if (loading) return <Loading label="Loading requests…" minHeight={200} />;
  if (error)
    return (
      <Empty icon="wrench" title="Requests unavailable" hint="We couldn't load maintenance for this tenancy." minHeight={200}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  if (reqs.length === 0)
    return <Empty icon="wrench" title="No maintenance requests" hint="Issues this tenant raises, and ones you log, will appear here." minHeight={200} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {reqs.map((r) => {
        const done = r.status === 'done';
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: '12px 14px' }}>
            <span style={{ width: 34, height: 34, borderRadius: 9, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
              <Icon n="wrench" size={15} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ color: C.ink, fontWeight: 600, fontSize: 13.8, display: 'block', textTransform: 'capitalize' }}>{r.category}</b>
              <small style={{ color: C.muted, fontSize: 12, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || 'No description'}</small>
            </div>
            <span style={{ ...chipTenant, color: done ? C.hi : C.accent2, background: done ? C.greenSoft : C.soft }}>{REQ_STATUS_LABEL[r.status] ?? r.status}</span>
          </div>
        );
      })}
      <div style={{ fontSize: 12, color: C.muted, paddingTop: 2 }}>Update status and add notes on the Maintenance page.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, background: C.accent, color: '#fff', border: 0, borderRadius: 9, padding: '8px 13px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const retryBtn: React.CSSProperties = { marginTop: 4, border: `1px solid ${C.line}`, background: C.card, color: C.accent, fontWeight: 600, fontSize: 13, padding: '8px 16px', borderRadius: 9, cursor: 'pointer' };
const ghostSm: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.card, color: C.accent, border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 10px', fontWeight: 600, fontSize: 12, cursor: 'pointer', flex: 'none' };
const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em' };
const input: React.CSSProperties = { border: `1px solid ${C.line}`, borderRadius: 9, padding: '9px 11px', fontSize: 13.5, color: C.ink, background: '#fff', fontFamily: 'inherit', textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 };
